import React, { useState, useEffect } from 'react';
import './CentralizedRequests.css';

const CentralizedRequests = () => {
  const [locations, setLocations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [modifiedRequests, setModifiedRequests] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [locationsRes, requestsRes] = await Promise.all([
          fetch('/api/locations/getAllLocations'),
          fetch('/api/hygiene/requests?status=PENDING')
        ]);
        
        const locationsData = await locationsRes.json();
        const requestsData = await requestsRes.json();
        
        setLocations(locationsData);
        setRequests(requestsData);
        
        // Initialiser les modifications
        const initialModifications = {};
        requestsData.forEach(request => {
          initialModifications[request._id] = {
            quantity: request.quantity,
            price: request.price || 0,
            controllerComment: '' // ← CHANGEMENT: Renommer reason en controllerComment
          };
        });
        setModifiedRequests(initialModifications);
      } catch (error) {
        console.error('Erreur chargement données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleQuantityChange = (requestId, newQuantity) => {
    if (newQuantity < 1) newQuantity = 1;
    
    setModifiedRequests(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        quantity: newQuantity
      }
    }));
  };

  const incrementQuantity = (requestId) => {
    const currentQuantity = modifiedRequests[requestId]?.quantity || 1;
    handleQuantityChange(requestId, currentQuantity + 1);
  };

  const decrementQuantity = (requestId) => {
    const currentQuantity = modifiedRequests[requestId]?.quantity || 1;
    if (currentQuantity > 1) {
      handleQuantityChange(requestId, currentQuantity - 1);
    }
  };

  const handlePriceChange = (requestId, price) => {
    setModifiedRequests(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        price: price || 0
      }
    }));
  };

  const handleControllerCommentChange = (requestId, controllerComment) => {
    setModifiedRequests(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        controllerComment: controllerComment
      }
    }));
  };

  // NOUVEAU: Confirmation d'une demande individuelle
  const confirmSingleRequest = async (requestId) => {
    const request = requests.find(req => req._id === requestId);
    if (!request) return;

    const modifications = modifiedRequests[requestId];
    const originalQuantity = request.quantity;
    const newQuantity = modifications?.quantity || originalQuantity;
    const price = modifications?.price || 0;
    const controllerComment = modifications?.controllerComment || '';

    try {
      const response = await fetch(`/api/hygiene/requests/${requestId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: 'APPROVED', 
          quantity: newQuantity,
          price: price,
          approvedBy: localStorage.getItem('username') || 'Contrôleur',
          modificationReason: controllerComment, // ← ENVOYER le commentaire contrôleur
          originalQuantity: originalQuantity
        }),
      });
      
      if (response.ok) {
        const updatedRequest = await response.json();
        
        // Mettre à jour l'état local
        setRequests(prev => prev.filter(req => req._id !== requestId));
        
        // Supprimer les modifications pour cette demande
        setModifiedRequests(prev => {
          const newModifications = { ...prev };
          delete newModifications[requestId];
          return newModifications;
        });
        
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } else {
        console.error('Erreur API:', response.status);
        throw new Error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour demande:', error);
      alert('Une erreur est survenue lors de la validation de la demande');
    }
  };

  // ANCIENNE FONCTION (gardée pour référence mais non utilisée)
  const confirmLocationRequests = async (location) => {
    const locationRequests = requests.filter(req => 
      req.location === location && req.status === 'PENDING'
    );

    const updatePromises = locationRequests.map(async (request) => {
      const modifications = modifiedRequests[request._id];
      const originalQuantity = request.quantity;
      const newQuantity = modifications?.quantity || originalQuantity;
      const price = modifications?.price || 0;
      const controllerComment = modifications?.controllerComment || '';

      try {
        const response = await fetch(`/api/hygiene/requests/${request._id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            status: 'APPROVED', 
            quantity: newQuantity,
            price: price,
            approvedBy: localStorage.getItem('username') || 'Contrôleur',
            modificationReason: controllerComment,
            originalQuantity: originalQuantity
          }),
        });
        
        if (response.ok) {
          return await response.json();
        } else {
          console.error('Erreur API:', response.status);
          throw new Error('Erreur lors de la mise à jour');
        }
      } catch (error) {
        console.error('Erreur mise à jour demande:', error);
        throw error;
      }
    });

    try {
      await Promise.all(updatePromises);
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      
      // Recharger les demandes
      const response = await fetch('/api/hygiene/requests?status=PENDING');
      const data = await response.json();
      setRequests(data);
      
      // Réinitialiser les modifications pour les nouvelles demandes
      const newModifications = {};
      data.forEach(request => {
        newModifications[request._id] = {
          quantity: request.quantity,
          price: request.price || 0,
          controllerComment: ''
        };
      });
      setModifiedRequests(newModifications);
      
    } catch (error) {
      console.error('Erreur lors de la confirmation:', error);
      alert('Une erreur est survenue lors de la validation des demandes');
    }
  };

  const getLocationSummary = (location) => {
    const locationRequests = requests.filter(req => req.location === location);
    const pending = locationRequests.filter(req => req.status === 'PENDING');
    return { pending };
  };

  const getQuantityChange = (request) => {
    const original = request.quantity;
    const modified = modifiedRequests[request._id]?.quantity || original;
    const difference = modified - original;
    
    if (difference > 0) return { type: 'increase', value: difference };
    if (difference < 0) return { type: 'decrease', value: Math.abs(difference) };
    return { type: 'same', value: 0 };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Calcul des statistiques
  const totalPending = requests.filter(req => req.status === 'PENDING').length;
  const totalLocations = locations.filter(location => {
    const { pending } = getLocationSummary(location.name);
    return pending.length > 0;
  }).length;

  if (loading) {
    return (
      <div className="centralized-container">
        <div className="empty-state">
          <div className="loading-spinner"></div>
          <div>Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="centralized-container">
      <div className="centralized-header">
        <h2>Centralisation des Demandes</h2>
        <p>Modifiez les quantités et prix si nécessaire</p>
      </div>

      {/* Statistiques compactes */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-number">{totalPending}</div>
          <div className="stat-label">En attente</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{totalLocations}</div>
          <div className="stat-label">Locations</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{locations.length}</div>
          <div className="stat-label">Total</div>
        </div>
      </div>

      {showSuccess && (
        <div className="success-message">
          ✅ Demande validée avec succès
        </div>
      )}

      {/* Liste des locations */}
      {locations.map(location => {
        const { pending } = getLocationSummary(location.name);
        if (pending.length === 0) return null;
        
        return (
          <div key={location._id} className="location-card">
            <div className="location-header">
              <div>
                <h3 className="location-title">{location.name}</h3>
                <div className="location-badge">
                  {pending.length} demande(s)
                </div>
              </div>
            </div>

            <div className="requests-list">
              {pending.map((request) => {
                const quantityChange = getQuantityChange(request);
                const modifications = modifiedRequests[request._id] || {};
                
                return (
                  <div key={request._id} className="request-item">
                    <div className="request-header">
                      <div className="request-product">{request.productName}</div>
                      <div className="request-details">
                        <span className="request-quantity">
                          {request.quantity} {request.unit}
                        </span>
                        <span className="request-requester">
                          {request.requestedBy}
                        </span>
                        <span className="request-date">
                          {formatDate(request.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Affichage du commentaire de l'utilisateur */}
                    {request.comments && (
                      <div className="user-comment-section">
                        <div className="user-comment-label">Commentaire de l'utilisateur:</div>
                        <div className="user-comment-content">
                          {request.comments}
                        </div>
                      </div>
                    )}

                    <div className="request-controls">
                      {/* Quantité */}
                      <div className="control-group">
                        <div className="control-label">Quantité</div>
                        <div className="quantity-controls">
                          <button
                            type="button"
                            className="quantity-btn"
                            onClick={() => decrementQuantity(request._id)}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={modifications.quantity || request.quantity}
                            onChange={(e) => handleQuantityChange(request._id, parseInt(e.target.value) || 1)}
                            className="quantity-input"
                          />
                          <button
                            type="button"
                            className="quantity-btn"
                            onClick={() => incrementQuantity(request._id)}
                          >
                            +
                          </button>
                        </div>
                        <div className="original-quantity">
                          Original: {request.quantity}
                        </div>
                        {quantityChange.type !== 'same' && (
                          <div className={`quantity-change ${
                            quantityChange.type === 'increase' ? 'quantity-increased' : 'quantity-decreased'
                          }`}>
                            {quantityChange.type === 'increase' ? '↑' : '↓'} 
                            {quantityChange.value}
                          </div>
                        )}
                      </div>

                      {/* Prix */}
                      <div className="control-group">
                        <div className="control-label">Prix unitaire</div>
                        <div className="price-section">
                          <input
                            type="number"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            value={modifications.price || ''}
                            onChange={(e) => handlePriceChange(request._id, parseFloat(e.target.value))}
                            className="price-input"
                          />
                          <span className="price-currency">DT</span>
                        </div>
                      </div>
                    </div>

                    {/* Commentaire du contrôleur */}
                    <div className="control-group">
                      <div className="control-label">Commentaire du contrôleur</div>
                      <textarea
                        placeholder="Ajoutez un commentaire pour justifier vos modifications..."
                        value={modifications.controllerComment || ''}
                        onChange={(e) => handleControllerCommentChange(request._id, e.target.value)}
                        className="reason-textarea"
                      />
                    </div>

                    {/* Indicateurs */}
                    {(quantityChange.type !== 'same' || modifications.price > 0) && (
                      <div className="modification-indicators">
                        {quantityChange.type !== 'same' && (
                          <div className="modification-badge quantity-modified">
                            Quantité modifiée
                          </div>
                        )}
                        {modifications.price > 0 && (
                          <div className="modification-badge price-modified">
                            Prix: {modifications.price} DT
                          </div>
                        )}
                      </div>
                    )}

                    {/* Bouton de validation individuel */}
                    <div className="request-actions">
                      <button
                        onClick={() => confirmSingleRequest(request._id)}
                        className="confirm-single-btn"
                      >
                        Valider cette demande
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CentralizedRequests;